// src/Admin/Dashboard.jsx — live overview
import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../Firebase/firebaseConfig';
import { enrollmentStatus, messageStatus, toDate, formatDateTime } from './utils';

const DAYS = 14;

const Dashboard = ({ enrollments, messages, onNavigate }) => {
  const [counts, setCounts] = useState(null);

  // Content collections are read once (they change rarely)
  useEffect(() => {
    let cancelled = false;
    const read = async (name) => {
      try {
        return (await getDocs(collection(db, name))).docs.map((d) => d.data());
      } catch (e) {
        console.error(`Dashboard: could not read ${name}`, e);
        return null;
      }
    };
    (async () => {
      const [courses, diplomas, blogs, books] = await Promise.all(['courses', 'diplomas', 'blogs', 'books'].map(read));
      if (cancelled) return;
      setCounts({
        courses: courses?.length ?? '—',
        diplomas: diplomas?.length ?? '—',
        blogs: blogs?.length ?? '—',
        books: books?.length ?? '—',
        open: [...(courses || []), ...(diplomas || [])].filter((p) => p.enrollmentOpen).length,
        programs: (courses?.length || 0) + (diplomas?.length || 0),
      });
    })();
    return () => { cancelled = true; };
  }, []);

  const newEnrollments = enrollments.filter((e) => enrollmentStatus(e) === 'new').length;
  const newMessages = messages.filter((m) => messageStatus(m) === 'new').length;

  const recentEnrollments = useMemo(
    () => [...enrollments].sort((a, b) => (toDate(b.timestamp)?.getTime() || 0) - (toDate(a.timestamp)?.getTime() || 0)).slice(0, 5),
    [enrollments]
  );
  const recentMessages = useMemo(
    () => [...messages].sort((a, b) => (toDate(b.timestamp)?.getTime() || 0) - (toDate(a.timestamp)?.getTime() || 0)).slice(0, 5),
    [messages]
  );

  // Enrollments per day for the last 14 days
  const trend = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (DAYS - 1));
    const days = Array.from({ length: DAYS }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return { date: d, count: 0 };
    });
    enrollments.forEach((e) => {
      const d = toDate(e.timestamp);
      if (!d) return;
      const idx = Math.floor((d - start) / 86400000);
      if (idx >= 0 && idx < DAYS) days[idx].count += 1;
    });
    return { days, max: Math.max(1, ...days.map((d) => d.count)), total: days.reduce((n, d) => n + d.count, 0) };
  }, [enrollments]);

  const stats = [
    { label: 'Courses', value: counts?.courses, icon: 'fa-book-open', go: 'courses', note: counts ? `${counts.open} of ${counts.programs} programs open` : '' },
    { label: 'Diplomas', value: counts?.diplomas, icon: 'fa-graduation-cap', go: 'diplomas' },
    { label: 'Blog posts', value: counts?.blogs, icon: 'fa-newspaper', go: 'blogs' },
    { label: 'Books', value: counts?.books, icon: 'fa-book', go: 'library' },
    { label: 'Enrollments', value: enrollments.length, icon: 'fa-user-graduate', go: 'enrollments', note: newEnrollments ? `${newEnrollments} new` : 'No new applicants', hot: newEnrollments > 0 },
    { label: 'Messages', value: messages.length, icon: 'fa-envelope', go: 'messages', note: newMessages ? `${newMessages} unread` : 'All handled', hot: newMessages > 0 },
  ];

  const actions = [
    { label: 'Review new enrollments', icon: 'fa-user-check', go: 'enrollments' },
    { label: 'Read messages', icon: 'fa-inbox', go: 'messages' },
    { label: 'Write a blog post', icon: 'fa-pen-to-square', go: 'blogs' },
    { label: 'Add a course', icon: 'fa-plus', go: 'courses' },
    { label: 'Add a book', icon: 'fa-book-medical', go: 'library' },
  ];

  return (
    <div className="adm-dash">
      <section className="adm-stats">
        {stats.map((s) => (
          <button key={s.label} className={`adm-stat ${s.hot ? 'is-hot' : ''}`} onClick={() => onNavigate(s.go)}>
            <span className="adm-stat__icon"><i className={`fas ${s.icon}`}></i></span>
            <span className="adm-stat__value">{s.value ?? '…'}</span>
            <span className="adm-stat__label">{s.label}</span>
            {s.note && <span className="adm-stat__note">{s.note}</span>}
          </button>
        ))}
      </section>

      <div className="adm-dash__grid">
        <section className="adm-panel">
          <header className="adm-panel__head">
            <h2>Enrollments — last {DAYS} days</h2>
            <span className="adm-pill">{trend.total}</span>
          </header>
          <div className="adm-bars" role="img" aria-label={`${trend.total} enrollments in the last ${DAYS} days`}>
            {trend.days.map((d) => (
              <div className="adm-bars__col" key={d.date.toISOString()} title={`${d.date.toLocaleDateString()}: ${d.count}`}>
                <span className="adm-bars__bar" style={{ height: `${(d.count / trend.max) * 100}%` }} data-empty={d.count === 0}></span>
                <small>{d.date.getDate()}</small>
              </div>
            ))}
          </div>
        </section>

        <section className="adm-panel">
          <header className="adm-panel__head"><h2>Quick actions</h2></header>
          <div className="adm-actions">
            {actions.map((a) => (
              <button key={a.label} className="adm-action" onClick={() => onNavigate(a.go)}>
                <i className={`fas ${a.icon}`}></i> {a.label} <i className="fas fa-arrow-right adm-action__go"></i>
              </button>
            ))}
          </div>
        </section>

        <section className="adm-panel">
          <header className="adm-panel__head">
            <h2>Latest enrollments</h2>
            <button className="adm-link" onClick={() => onNavigate('enrollments')}>View all</button>
          </header>
          {recentEnrollments.length ? (
            <ul className="adm-feed">
              {recentEnrollments.map((e) => (
                <li key={e.id}>
                  <span className="adm-avatar">{(e.name || '?').trim().charAt(0).toUpperCase()}</span>
                  <div>
                    <strong>{e.name || 'Unknown'}</strong>
                    <small>{e.programName || 'Program'} · {formatDateTime(e.timestamp)}</small>
                  </div>
                  <span className={`adm-status adm-status--${enrollmentStatus(e)}`}>{enrollmentStatus(e)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="adm-empty">No enrollments yet.</p>
          )}
        </section>

        <section className="adm-panel">
          <header className="adm-panel__head">
            <h2>Latest messages</h2>
            <button className="adm-link" onClick={() => onNavigate('messages')}>View all</button>
          </header>
          {recentMessages.length ? (
            <ul className="adm-feed">
              {recentMessages.map((m) => (
                <li key={m.id}>
                  <span className="adm-avatar">{(m.name || '?').trim().charAt(0).toUpperCase()}</span>
                  <div>
                    <strong>{m.subject || '(no subject)'}</strong>
                    <small>{m.name || 'Unknown'} · {formatDateTime(m.timestamp)}</small>
                  </div>
                  <span className={`adm-status adm-status--${messageStatus(m)}`}>{messageStatus(m)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="adm-empty">No messages yet.</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
